import { GateHours, GateHoursDailyOverride } from '../../../common/service/gate-hours/types';

const initialEntry = { remarks: '', timeSlots: [] };

export type GroupedDailyOverride = {
  date: string;
  remarks: string;
  timeSlots: { from: string; to: string }[];
  fullDayOpen: boolean;
};

const formatTime = (time: string) => (time.includes('T') ? time.substring(11, 16) : time.substring(0, 5));

const normalizeDateKey = (date: string) => date.split('T')[0];

export const parseData = (data: GateHours | null) =>
  data
    ? [
        data.days.MONDAY || initialEntry,
        data.days.TUESDAY || initialEntry,
        data.days.WEDNESDAY || initialEntry,
        data.days.THURSDAY || initialEntry,
        data.days.FRIDAY || initialEntry,
        data.days.SATURDAY || initialEntry,
        data.days.SUNDAY || initialEntry,
      ]
    : [];

export const parseHours = (timeSlots: { from: string; to: string }[]) => {
  if (!timeSlots.length) return '';
  return timeSlots.map((timeSlot) => `${formatTime(timeSlot.from)} - ${formatTime(timeSlot.to)}`).join(' / ');
};

export const groupDailyOverridesByDate = (
  data: GateHoursDailyOverride[] | null,
): Record<string, GroupedDailyOverride> => {
  if (!data?.length) return {};

  return data.reduce<Record<string, GroupedDailyOverride>>((acc, entry) => {
    const dateKey = normalizeDateKey(entry.date);

    if (!acc[dateKey]) {
      acc[dateKey] = {
        date: dateKey,
        remarks: entry.remarks || '',
        timeSlots: [],
        fullDayOpen: Boolean(entry.fullDayOpen),
      };
    }

    if (entry.remarks && !acc[dateKey].remarks) {
      acc[dateKey].remarks = entry.remarks;
    }

    if (entry.fullDayOpen) {
      acc[dateKey].fullDayOpen = true;
    }

    if (entry.from && entry.to) {
      acc[dateKey].timeSlots.push({ from: entry.from, to: entry.to });
    }

    return acc;
  }, {});
};

export const createDTO = (data: GateHours, values): GateHours => {
  const selectedDays = values.selectedDays;

  const createDay = (day: string) =>
    selectedDays.includes(day)
      ? {
          [day]: {
            remarks: values.remarks,
            fullDayOpen: values.fullDayOpen || false,
            timeSlots: values.open
              ? values.fullDayOpen
                ? []
                : [
                    { from: `2025-01-01T${values.from}:00`, to: `2025-01-01T${values.to}:00` },
                    ...(values.from1
                      ? [{ from: `2025-01-01T${values.from1}:00`, to: `2025-01-01T${values.to1}:00` }]
                      : []),
                    ...(values.from2
                      ? [{ from: `2025-01-01T${values.from2}:00`, to: `2025-01-01T${values.to2}:00` }]
                      : []),
                  ]
              : null,
          },
        }
      : data.days[day]
        ? { [day]: data.days[day] }
        : {};

  const newDays = {
    ...createDay('MONDAY'),
    ...createDay('TUESDAY'),
    ...createDay('WEDNESDAY'),
    ...createDay('THURSDAY'),
    ...createDay('FRIDAY'),
    ...createDay('SATURDAY'),
    ...createDay('SUNDAY'),
  };

  return {
    ...data,
    // @ts-ignore
    days: newDays,
  };
};

export const createDailyOverrideDTOs = (date: string, values): GateHoursDailyOverride[] => {
  const remarks = values.remarks;

  if (!values.open) {
    return [
      {
        date,
        remarks,
        fullDayOpen: false,
      },
    ];
  }

  if (values.fullDayOpen) {
    return [
      {
        date,
        remarks,
        fullDayOpen: true,
      },
    ];
  }

  const overrides: GateHoursDailyOverride[] = [];

  const addSlot = (from?: string, to?: string) => {
    if (!from || !to) return;

    overrides.push({
      date,
      remarks,
      fullDayOpen: false,
      from: `${from}:00`,
      to: `${to}:00`,
    });
  };

  addSlot(values.from, values.to);
  addSlot(values.from1, values.to1);
  addSlot(values.from2, values.to2);

  return overrides;
};
